import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { forIn, isFunction, map, sortBy } from 'lodash';
import { UiActions } from '@app/store/actions';
const pdfMergerJs = require('pdf-merger-js');
import { TDocumentDefinitions } from 'pdfmake/interfaces';

import { lazyInject } from '@app/containers/base/decorators';
import { dataURLtoFile } from '@app/shared/utils';

import { font as SourceSansPro } from './SourceSansPro';
import { PdfWorkResponse, PdfWorkerSelfType } from './pdf-worker.type';
import { footer } from './v2/utils/pdf-footer.utils';

@Injectable({
  providedIn: 'root',
})
export class PdfWorker {
  @lazyInject(UiActions) public uiActions: UiActions;

  private worker: Worker;

  public constructor(public injector: Injector) {
    this.initWorker();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async openPdf(
    docDefinition: TDocumentDefinitions,
    globalOptions: PdfWorkerSelfType,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise((resolve, reject): void => {
      stringifyFunc(docDefinition);
      docDefinition['mode'] = 'single';
      this.worker.postMessage({ globalOptions, mode: 'setGlobalOptions' });
      this.worker.postMessage(docDefinition);
      // Received message from worker thread
      this.worker.onmessage = (res: PdfWorkResponse): void => {
        if (!res?.data?.pdfBlob) {
          return;
        }
        this.uiActions.disableLoader();
        const url = window.URL.createObjectURL(res.data.pdfBlob);
        try {
          window.open(url);
        } catch (err) {}
        resolve();
      };
    });
  }

  public async printPdf(
    docDefinition: TDocumentDefinitions,
    globalOptions: PdfWorkerSelfType,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise((resolve, reject): void => {
      stringifyFunc(docDefinition);
      docDefinition['mode'] = 'single';
      this.worker.postMessage({ globalOptions, mode: 'setGlobalOptions' });
      this.worker.postMessage(docDefinition);
      // Received message from worker thread
      this.worker.onmessage = (res: PdfWorkResponse): void => {
        if (!res?.data?.pdfBlob) {
          return;
        }
        const url = window.URL.createObjectURL(res.data.pdfBlob);
        try {
          const win = window.open(url);
          win.focus();
          win.print();
        } catch (err) {}
        resolve();
      };
    });
  }

  public async exportPdf(
    docDefinition: TDocumentDefinitions,
    globalOptions: PdfWorkerSelfType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fileName: string,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise((resolve, reject): void => {
      stringifyFunc(docDefinition);
      docDefinition['mode'] = 'single';
      this.worker.postMessage({ globalOptions, mode: 'setGlobalOptions' });
      this.worker.postMessage(docDefinition);
      // Received message from worker thread
      this.worker.onmessage = (res: PdfWorkResponse): void => {
        if (!res?.data?.pdfBlob) {
          return;
        }
        const url = window.URL.createObjectURL(res.data.pdfBlob);
        try {
          window.open(url);
        } catch (err) {}
        resolve();
      };
    });
  }

  public async bulkPrintPdf(
    docDefinitionList: TDocumentDefinitions[],
    globalOptions: PdfWorkerSelfType,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise(async (resolve, reject): Promise<void> => {
      this.worker.postMessage({ globalOptions, mode: 'setGlobalOptions' });
      await Promise.all(
        docDefinitionList.map((docDefinition) => {
          stringifyFunc(docDefinition);
          docDefinition['mode'] = 'bulk';
          this.worker.postMessage(docDefinition);
        }),
      );
      const base64PdfDataList = [];
      // Received message from worker thread
      this.worker.onmessage = async (res: PdfWorkResponse): Promise<void> => {
        if (!res?.data?.pdfBase64) {
          return;
        }
        base64PdfDataList.push({
          sortNumber: res.data.sortNumber,
          pdfBase64: res.data.pdfBase64,
        });
        if (base64PdfDataList.length === docDefinitionList.length) {
          const url = await this.getMergedFileObjUrl(
            map(
              sortBy(base64PdfDataList, 'sortNumber'),
              (item) => item.pdfBase64,
            ),
          );
          try {
            window.open(url).print();
          } catch (err) {}
          resolve();
        }
      };
    });
  }

  public async bulkExportPdf(
    docDefinitionList: TDocumentDefinitions[],
    globalOptions: PdfWorkerSelfType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    filename: string,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return new Promise(async (resolve, reject): Promise<void> => {
      this.worker.postMessage({ globalOptions, mode: 'setGlobalOptions' });
      await Promise.all(
        docDefinitionList.map((docDefinition) => {
          stringifyFunc(docDefinition);
          docDefinition['mode'] = 'bulk';
          this.worker.postMessage(docDefinition);
        }),
      );
      const base64PdfDataList = [];
      // Received message from worker thread
      this.worker.onmessage = async (res: PdfWorkResponse): Promise<void> => {
        if (!res?.data?.pdfBase64) {
          return;
        }
        base64PdfDataList.push({
          sortNumber: res.data.sortNumber,
          pdfBase64: res.data.pdfBase64,
        });
        if (base64PdfDataList.length === docDefinitionList.length) {
          const url = await this.getMergedFileObjUrl(
            map(
              sortBy(base64PdfDataList, 'sortNumber'),
              (item) => item.pdfBase64,
            ),
          );
          try {
            const win = window.open(url);
            win.focus();
          } catch (err) {}
          resolve();
        }
      };
    });
  }

  public async getMergedFileObjUrl(fileUrls: string[]): Promise<string> {
    const merger = new pdfMergerJs();
    for (const fileUrl of fileUrls) {
      const file = dataURLtoFile(
        `data:application/pdf;base64,${fileUrl}`,
        `${Math.random()}.pdf`,
      );
      await merger.add(file);
    }
    const mergedPdfBlob = await merger.saveAsBlob();

    return URL.createObjectURL(mergedPdfBlob);
  }

  private initWorker(): void {
    const origin = location.origin;
    const initSourceSansProFont = Object.entries(SourceSansPro)
      .map(([key, value]) => {
        return `self.pdfMake.vfs['${key}'] = '${value}';`;
      })
      .join(';');

    const escapeTemplateLiteral = (source: string): string =>
      source.replace(/`/g, '\\`').replace(/\$\{/g, '\\${');

    const parseFuncSource = escapeTemplateLiteral(parseFunc.toString());
    const setGlobalOptionsSource = escapeTemplateLiteral(
      setGlobalOptions.toString(),
    );
    const bulkCreatePdfAsBase64Source = escapeTemplateLiteral(
      bulkCreatePdfAsBase64.toString(),
    );
    const singleCreatePdfAsBlobSource = escapeTemplateLiteral(
      singleCreatePdfAsBlob.toString(),
    );
    const onmessageHandleSource = escapeTemplateLiteral(
      onmessageHandle.toString(),
    );

    const blob = new Blob(
      [
        `
      importScripts('${origin}/assets/pdfmake/pdfmake.min.js');
      self.tempPdfMake = self.pdfMake;

      importScripts('${origin}/assets/pdfmake/vfs_fonts.js');
      self.tempPdfMake.vfs = self.pdfMake.vfs;
      self.pdfMake = self.tempPdfMake;

      ${initSourceSansProFont}

      self.pdfMake.fonts = {
          Roboto: {
              normal: 'Roboto-Regular.ttf',
              bold: 'Roboto-Medium.ttf',
              italics: 'Roboto-Italic.ttf',
              bolditalics: 'Roboto-MediumItalic.ttf'
          },
          SourceSansPro: {
              normal: 'SourceSansPro-Regular.ttf',
              bold: 'SourceSansPro-Bold.ttf',
              italics: 'SourceSansPro-Italic.ttf',
              bolditalics: 'SourceSansPro-BoldItalic.ttf'
          }
      };

      // Define table row colors
      self.greyBgColor = '#FAFAFA';
      self.whiteBgColor = '#FFFFFF';

      const parseFunc = new Function(\`(${parseFuncSource})(...arguments)\`);
      const ${setGlobalOptions.name} = new Function(\`(${setGlobalOptionsSource})(...arguments)\`);
      const ${bulkCreatePdfAsBase64.name} = new Function(\`(${bulkCreatePdfAsBase64Source})(...arguments)\`);
      const ${singleCreatePdfAsBlob.name} = new Function(\`(${singleCreatePdfAsBlobSource})(...arguments)\`);
      const onmessageHandle = new Function(\`(${onmessageHandleSource})(...arguments)\`);

      onmessage = (req) => {
          // add pdf footer here
          if (['single', 'bulk'].includes(req.data?.mode)) {
            req.data.footer = ${footer};
          }
          // avoid build cause function name changed
          parseFunc(req.data);
          onmessageHandle(req);
      }
      `,
      ],
      { type: 'text/javascript' },
    );

    this.worker = new Worker(URL.createObjectURL(blob));
  }
}

// worker runtime
// eslint-disable-next-line
export function onmessageHandle(req) {
  switch (req.data.mode) {
    case 'setGlobalOptions':
      setGlobalOptions(req);
      break;
    case 'single':
      singleCreatePdfAsBlob(req);
      break;
    case 'bulk':
      bulkCreatePdfAsBase64(req);
      break;
    default:
  }
}

// eslint-disable-next-line
function setGlobalOptions(req) {
  // Set global variables into worker, variables used in header/footer function, etc.
  const globalOptions: PdfWorkerSelfType = req.data.globalOptions;
  self['createDate'] = globalOptions.createDate;
  self['serverDate'] = globalOptions.serverDate;
  self['allRightsText'] = globalOptions.allRightsText;
  self['footerText'] = globalOptions.footerText;
  self['i18n'] = globalOptions.i18n ?? {};
}

// eslint-disable-next-line
function bulkCreatePdfAsBase64(req) {
  self['summary'] = req.data.globalSummary;
  new Promise((resolve, reject): void => {
    try {
      // @ts-ignore
      pdfMake.createPdf(req.data).getBase64((res) => {
        if (res) {
          resolve({ sortNumber: req.data.sortNumber, pdfBase64: res });
        } else {
          reject();
        }
      });
    } catch (err) {
      reject(err);
    }
  })
    .then((res) => {
      // @ts-ignore
      postMessage({ pdfBase64: res.pdfBase64, sortNumber: res.sortNumber });
    })
    .finally();
}

// eslint-disable-next-line
function singleCreatePdfAsBlob(req) {
  new Promise((resolve, reject): void => {
    try {
      // @ts-ignore
      pdfMake.createPdf(req.data).getBlob((res) => {
        if (res) {
          resolve(res);
        } else {
          reject();
        }
      });
    } catch (err) {
      reject(err);
    }
  })
    .then((pdfBlob) => {
      // @ts-ignore
      postMessage({ pdfBlob });
    })
    .finally();
}

// eslint-disable-next-line
function stringifyFunc(obj) {
  forIn(obj, (value: any, key: string): void => {
    if (typeof value === 'function') {
      obj[key] = `<function>${(<Function>value).toString()}</function>`;
    }
    if (typeof value === 'object') {
      stringifyFunc(value as object);
    }
  });
}

// worker runtime
// eslint-disable-next-line
function parseFunc(obj) {
  const reg = new RegExp('(<function>)(.*?)(</function>)', 's');
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (typeof value === 'string' && reg.test(value)) {
      const execRes = reg.exec(value);
      const funcSource = execRes?.[2];
      // Build the function safely and assign directly
      if (funcSource) {
        if (key === 'pageBreakBefore') {
          // eslint-disable-next-line
          obj[key] = new Function(`return (${funcSource});`)();
        } else {
          // eslint-disable-next-line
          obj[key] = new Function(`return (${funcSource})(...arguments);`);
        }
      }
    }
    if (typeof value === 'object' && value !== null) {
      parseFunc(value);
    }
  });
}
